import {
  PalmettoButton,
  PalmettoCheckbox,
  PalmettoIcon,
  PalmettoTag,
  PalmettoTextInput,
} from '@palmetto/dsm-components';
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useId,
  useState,
} from 'react';

const LIST_MAX_HEIGHT_CLASS = 'max-h-[280px]';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Shorter text for the selected chip, e.g. a state code instead of "Alabama (AL)". */
  chipLabel?: string;
}

export interface SearchableMultiSelectProps {
  id?: string;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  validationMessage?: { type: 'error'; text: string };
  helperText?: string;
  emptyMessage?: string;
  /** Collapses a full selection into one chip, e.g. "All states (50)". */
  allSelectedLabel?: string;
  loading?: boolean;
  disabled?: boolean;
}

interface OptionRowProps {
  option: MultiSelectOption;
  selected: boolean;
  onToggle: (value: string, selected: boolean) => void;
}

const OptionRow = ({ option, selected, onToggle }: OptionRowProps) => {
  const handleClick = useCallback(
    (event: ReactMouseEvent) => {
      // A click on the label text makes the browser re-dispatch a click on the checkbox input,
      // which would toggle a second time; only the original click counts.
      if (event.target instanceof HTMLInputElement) return;
      onToggle(option.value, !selected);
    },
    [onToggle, option.value, selected],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      onToggle(option.value, !selected);
    },
    [onToggle, option.value, selected],
  );

  return (
    <div
      role="option"
      aria-selected={selected}
      aria-label={option.label}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`cursor-pointer rounded-8 px-8 py-6 ${selected ? 'bg-canvas-success' : ''}`}
    >
      <div aria-hidden="true" className="pointer-events-none">
        <PalmettoCheckbox label={option.label} checked={selected} tabIndex={-1} size="small" />
      </div>
    </div>
  );
};

// The panel is plain CSS under a relative wrapper rather than a floating-ui popover: inside
// a transformed, overflow-scrolling dialog (e.g. a modal) the popover's positioning is unreliable.
export const SearchableMultiSelect = ({
  id,
  label,
  placeholder,
  searchPlaceholder,
  options,
  value,
  onChange,
  validationMessage,
  helperText,
  emptyMessage = 'No options found',
  allSelectedLabel,
  loading = false,
  disabled = false,
}: SearchableMultiSelectProps) => {
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const labelId = `${triggerId}-label`;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? options.filter((option) => option.label.toLowerCase().includes(term)) : options;
  }, [options, search]);
  const selectedOptions = useMemo(() => options.filter((option) => selected.has(option.value)), [options, selected]);

  const valueRef = useRef(value);
  const lastPropValueRef = useRef(value);
  if (lastPropValueRef.current !== value) {
    lastPropValueRef.current = value;
    valueRef.current = value;
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleTriggerClick = useCallback(() => {
    if (open) close();
    else setOpen(true);
  }, [open, close]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
    [],
  );

  const commit = useCallback(
    (next: string[]) => {
      valueRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const handleToggle = useCallback(
    (optionValue: string, checked: boolean) => {
      const current = valueRef.current;
      const without = current.filter((entry) => entry !== optionValue);
      commit(checked ? [...without, optionValue] : without);
    },
    [commit],
  );

  const handleSelectAll = useCallback(
    () => commit([...new Set([...valueRef.current, ...filtered.map((option) => option.value)])]),
    [commit, filtered],
  );

  const handleClearAll = useCallback(() => commit([]), [commit]);

  // Keeps focus in the search input: a blur would shift its floating label and move the row
  // between mousedown and mouseup, swallowing the click.
  const keepSearchFocus = useCallback((event: ReactMouseEvent) => event.preventDefault(), []);

  const handleSearchKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      // The field sits in a form, where Enter would submit it.
      event.preventDefault();
      if (filtered.length === 1) handleToggle(filtered[0].value, !selected.has(filtered[0].value));
    },
    [filtered, selected, handleToggle],
  );

  const isOpen = open && !disabled;
  const showAllSelectedChip =
    Boolean(allSelectedLabel) && options.length > 1 && selectedOptions.length === options.length;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) close();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Keep Escape from also dismissing the surrounding modal.
      event.stopPropagation();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, close]);

  // A scrolling container (e.g. a modal body) may clip the panel, so a panel opening below the fold would otherwise be hidden.
  useEffect(() => {
    if (isOpen) panelRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen]);
  const hasError = Boolean(validationMessage);
  const triggerBorder = hasError ? 'border-input-border-error' : 'border-input-border-default';

  return (
    <div className="flex flex-col gap-4">
      <span id={labelId} className="body-200 text-text-secondary">
        {label} <span className="text-text-error">*</span>
      </span>
      <div ref={wrapperRef} className="relative">
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          disabled={disabled}
          aria-labelledby={`${labelId} ${triggerId}`}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-invalid={hasError}
          onClick={handleTriggerClick}
          className={`flex min-h-[56px] w-full items-center justify-between gap-8 rounded-12 border bg-input-bg-default px-16 py-8 text-left disabled:opacity-50 ${triggerBorder}`}
        >
          {selectedOptions.length > 0 ? (
            <span className="flex flex-wrap gap-4">
              {showAllSelectedChip ? (
                <PalmettoTag label={`${allSelectedLabel} (${options.length})`} />
              ) : (
                selectedOptions.map((option) => (
                  <PalmettoTag key={option.value} label={option.chipLabel ?? option.label} />
                ))
              )}
            </span>
          ) : (
            <span className="body-300 text-text-tertiary">{placeholder}</span>
          )}
          <PalmettoIcon name="chevronDown" size="sm" />
        </button>
        {isOpen && (
          <div
            ref={panelRef}
            role="dialog"
            aria-label={label}
            className="absolute top-full left-0 z-50 mt-4 flex w-full min-w-[280px] flex-col gap-8 rounded-12 border border-input-border-default bg-canvas-default p-8 shadow-lg"
          >
            <PalmettoTextInput
              label={searchPlaceholder}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="flex gap-8" onMouseDown={keepSearchFocus}>
              <PalmettoButton type="button" emphasis="text" size="small" layout="hug" onClick={handleSelectAll}>
                {`Select all (${filtered.length})`}
              </PalmettoButton>
              <PalmettoButton type="button" emphasis="text" size="small" layout="hug" onClick={handleClearAll}>
                Clear all
              </PalmettoButton>
            </div>
            <div
              role="listbox"
              aria-multiselectable="true"
              aria-label={label}
              onMouseDown={keepSearchFocus}
              className={`flex flex-col gap-2 overflow-y-auto ${LIST_MAX_HEIGHT_CLASS}`}
            >
              {loading ? (
                <div className="p-8 body-200 text-text-secondary">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 body-200 text-text-secondary">{emptyMessage}</div>
              ) : (
                filtered.map((option) => (
                  <OptionRow
                    key={option.value}
                    option={option}
                    selected={selected.has(option.value)}
                    onToggle={handleToggle}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {validationMessage ? (
        <div role="alert" className="body-200 text-text-error">
          {validationMessage.text}
        </div>
      ) : (
        helperText && <div className="body-200 text-text-secondary">{helperText}</div>
      )}
    </div>
  );
};
